import { useCallback, useEffect, useState } from 'react';
import { fetchMe, logout as apiLogout, type AuthProviders, type AuthUser } from '../api';

export interface AuthState {
  user: AuthUser | undefined;
  providers: AuthProviders;
  /** Sant tills vi vet om det finns en session – undviker att inloggningsrutan blinkar förbi. */
  checking: boolean;
  setUser: (user: AuthUser) => void;
  signOut: () => Promise<void>;
}

const NO_PROVIDERS: AuthProviders = { sso: false, localAuth: false };

export function useAuth(): AuthState {
  const [user, setUserState] = useState<AuthUser>();
  const [providers, setProviders] = useState<AuthProviders>(NO_PROVIDERS);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const controller = new AbortController();
    fetchMe(controller.signal)
      .then((result) => {
        setUserState(result.user ?? undefined);
        setProviders({ sso: result.sso, localAuth: result.localAuth });
      })
      .catch(() => setUserState(undefined))
      .finally(() => setChecking(false));
    return () => controller.abort();
  }, []);

  const signOut = useCallback(async () => {
    await apiLogout().catch(() => undefined);
    setUserState(undefined);
  }, []);

  return { user, providers, checking, setUser: setUserState, signOut };
}

/** Läser token och adress ur en återställningslänk i adressfältet. */
export function readResetContext(): { token: string; email: string } | undefined {
  const hash = window.location.hash;
  if (!hash.startsWith('#aterstall')) return undefined;

  const params = new URLSearchParams(hash.slice(hash.indexOf('?') + 1));
  const token = params.get('token');
  const email = params.get('e');
  return token && email ? { token, email } : undefined;
}

const SSO_ERRORS: Record<string, string> = {
  unconfigured: 'Microsoft-inloggning är inte konfigurerad. Kontakta en administratör.',
  cancelled: 'Inloggningen avbröts.',
  failed: 'Inloggningen med Microsoft misslyckades. Försök igen.',
  domain: 'Kontot tillhör en e-postdomän som inte får använda verktyget.',
  disabled: 'Ditt konto är avstängt. Kontakta en administratör.',
};

/** Felet från SSO-återkomsten. Hashen rensas direkt så att det inte ligger kvar i historiken. */
export function readSsoError(): string | undefined {
  const hash = window.location.hash;
  if (!hash.startsWith('#sso_error=')) return undefined;

  const code = hash.slice('#sso_error='.length);
  window.history.replaceState(null, '', window.location.pathname + window.location.search);
  return SSO_ERRORS[code] ?? SSO_ERRORS.failed;
}
